import { useGetTagsList } from "@birdwatch/client";
import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { useMemo } from "react";
import { Badge } from "@/components/ui/badge/badge";
import { ComboboxMulti, type ComboboxOption } from "@/components/ui/combobox";

type TagsSelectProps = FieldWrapperInjectedProps & {
  className?: string;
  value: string[];
  onChange: (value: string[]) => void;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  disabled?: boolean;
  "data-test"?: string;
};

/** Picks order tags (ids) from every tag, A→Z. */
export function TagsSelect({
  className,
  value,
  onChange,
  defaultOpen = false,
  onOpenChange,
  modal = true,
  disabled,
  ...props
}: TagsSelectProps) {
  const { data: tags = [] } = useGetTagsList().take(-1).get();

  const options = useMemo<ComboboxOption[]>(() => {
    const collator = new Intl.Collator(undefined, {
      usage: "sort",
      sensitivity: "base",
      numeric: true,
    });
    return [...tags]
      .sort((a, b) => collator.compare(a.name, b.name))
      .map((tag) => ({ value: tag.id, label: tag.name }));
  }, [tags]);

  return (
    <ComboboxMulti
      {...props}
      value={value}
      onChange={onChange}
      options={options}
      indicator="checkbox"
      groupSelected
      disabled={disabled}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      modal={modal}
      className={className}
      searchPlaceholder="Search tags..."
      emptyText="No tag found."
      renderTrigger={(selected) =>
        selected.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {selected.map((option) => (
              <Badge key={option.value} shape="rounded" size="xs">
                {option.label}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-text-dark-min line-clamp-1">Select tags</p>
        )
      }
    />
  );
}
